import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  Connection,
  Edge,
  Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

interface Props {
  initialName?: string;
  initialConfig?: any;
  onSave: (name: string, config: any) => void;
  onCancel: () => void;
}

interface OptionItem {
  text: string;
  type: 'callback' | 'link';
  payload?: string;
  url?: string;
}

/* ---------- Кастомный узел ---------- */
const CustomNode = ({ data }: { data: any }) => {
  const options: OptionItem[] = data.options || [];
  const isMain = data.is_main || false;
  const format = data.format || '';

  return (
    <div style={{
      padding: '12px 16px',
      border: `2px solid ${isMain ? '#e6a23c' : '#4a90d9'}`,
      borderRadius: 10,
      background: '#fff',
      minWidth: 190,
      boxShadow: '0 3px 12px rgba(0,0,0,0.08)',
      position: 'relative',
      fontFamily: '"Inter", "Roboto", sans-serif',
    }}>
      <Handle type="target" position={Position.Top} style={{ background: '#555', width: 10, height: 10 }} />
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
        {isMain ? '🏠 Главное меню' : '📋 Меню'}
        {format && <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>{format}</span>}
      </div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#333' }}>{data.label}</div>
      {data.image_url && (
        <img src={data.image_url} style={{ maxWidth: '100%', maxHeight: 80, marginTop: 6, borderRadius: 6 }} alt="" />
      )}
      {options.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {options.map((opt, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <span style={{
                fontSize: 11, background: '#f5f5f5', padding: '3px 8px', borderRadius: 6,
                maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {opt.text || `Кнопка ${idx + 1}`}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={`out_${idx}`}
                style={{
                  background: '#e6a23c',
                  width: 12, height: 12,
                  position: 'absolute', right: -8, top: '50%', transform: 'translateY(-50%)',
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const nodeTypes = { custom: CustomNode };

/* ---------- Внутренний компонент ---------- */
const ScenarioBuilderInner: React.FC<Props> = ({ initialName = '', initialConfig, onSave, onCancel }) => {
  const [name, setName] = useState(initialName);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    (initialConfig?.nodes || []).map((n: any) => ({ ...n, data: { ...n.data, type: 'menu' } }))
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialConfig?.edges || []);
  const [editingNode, setEditingNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);

  const [editLabel, setEditLabel] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editFormat, setEditFormat] = useState('');
  const [editIsMain, setEditIsMain] = useState(false);
  const [editOptions, setEditOptions] = useState<OptionItem[]>([]);
  const [hasMainMenu, setHasMainMenu] = useState(false);

  const { deleteElements } = useReactFlow();

  useEffect(() => {
    const mainNode = nodes.find(n => n.data.is_main);
    setHasMainMenu(!!mainNode);
    if (editingNode && editingNode.data.is_main) setEditIsMain(true);
  }, [nodes, editingNode]);

  // Автообновление подписей на рёбрах
  useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        if (!sourceNode || sourceNode.data.type !== 'menu') return edge;
        const options: OptionItem[] = sourceNode.data.options || [];
        const match = edge.sourceHandle?.match(/^out_(\d+)$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          if (options[idx]) return { ...edge, label: options[idx].text || `Кнопка ${idx + 1}` };
        }
        return edge;
      })
    );
  }, [nodes, setEdges]);

  // Удаление по Delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete' && !editingNode) {
        const selectedNodes = nodes.filter((n) => n.selected);
        const selectedEdges = edges.filter((ed) => ed.selected);
        if (selectedNodes.length > 0 || selectedEdges.length > 0) {
          deleteElements({ nodes: selectedNodes, edges: selectedEdges });
          setSelectedEdge(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nodes, edges, deleteElements, editingNode]);

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => setSelectedEdge(edge), []);
  const deleteSelectedEdge = () => {
    if (selectedEdge) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdge.id));
      setSelectedEdge(null);
    }
  };

  const openEditor = useCallback((node: Node) => {
    const data = node.data;
    setEditLabel(data.label || '');
    setEditImageUrl(data.image_url || '');
    setEditFormat(data.format || '');
    setEditIsMain(data.is_main || false);
    setEditOptions(data.options || []);
    setEditingNode(node);
  }, []);

  const closeEditor = () => setEditingNode(null);

  const saveNodeChanges = () => {
    if (!editingNode) return;

    if (editIsMain) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === editingNode.id
            ? { ...n, data: { ...n.data, is_main: true } }
            : n.data.type === 'menu'
            ? { ...n, data: { ...n.data, is_main: false } }
            : n
        )
      );
    }

    // Переназначение sourceHandle при изменении порядка кнопок
    const oldOptions: OptionItem[] = editingNode.data.options || [];
    const newOptions = editOptions;
    if (JSON.stringify(oldOptions) !== JSON.stringify(newOptions)) {
      const oldIndexMap: Record<number, number> = {};
      for (let newIdx = 0; newIdx < newOptions.length; newIdx++) {
        const newOpt = newOptions[newIdx];
        const oldIdx = oldOptions.findIndex(
          (o: OptionItem) => o.text === newOpt.text && o.type === newOpt.type && o.payload === newOpt.payload
        );
        if (oldIdx !== -1) oldIndexMap[oldIdx] = newIdx;
      }
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.source !== editingNode.id) return edge;
          const match = edge.sourceHandle?.match(/^out_(\d+)$/);
          if (match) {
            const oldIdx = parseInt(match[1], 10);
            if (oldIndexMap[oldIdx] !== undefined) {
              const newIdx = oldIndexMap[oldIdx];
              return { ...edge, sourceHandle: `out_${newIdx}`, label: newOptions[newIdx]?.text || edge.label };
            } else return null;
          }
          return edge;
        }).filter(Boolean) as Edge[]
      );
    }

    const newData: any = {
      label: editLabel,
      type: 'menu',
      image_url: editImageUrl || null,
      format: editFormat || null,
      is_main: editIsMain,
      options: editOptions,
    };
    setNodes((nds) => nds.map((n) => (n.id === editingNode.id ? { ...n, data: newData } : n)));
    closeEditor();
  };

  const deleteNode = (nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    closeEditor();
  };

  const addMenuNode = () => {
    const newNode: Node = {
      id: `node_${Date.now()}`,
      type: 'custom',
      data: { label: 'Новое меню', type: 'menu', options: [] },
      position: { x: Math.random() * 300, y: Math.random() * 300 },
    };
    setNodes((nds) => nds.concat(newNode));
  };

  const onConnect = useCallback((connection: Connection) => {
    setEdges((eds) => addEdge({ ...connection, type: 'smoothstep', animated: true }, eds));
  }, [setEdges]);

  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => openEditor(node), [openEditor]);

  const handleSave = () => {
    if (!name.trim()) { alert('Введите название сценария'); return; }
    onSave(name, { nodes, edges });
  };

  const addOption = () => setEditOptions([...editOptions, { text: '', type: 'callback', payload: '' }]);
  const updateOption = (index: number, field: keyof OptionItem, value: string) => {
    const updated = [...editOptions];
    (updated[index] as any)[field] = value;
    setEditOptions(updated);
  };
  const removeOption = (index: number) => setEditOptions(editOptions.filter((_, i) => i !== index));
  const moveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...editOptions];
    if (direction === 'up' && index > 0) [newOptions[index - 1], newOptions[index]] = [newOptions[index], newOptions[index - 1]];
    else if (direction === 'down' && index < newOptions.length - 1) [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
    setEditOptions(newOptions);
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f2f5', padding: 32,
      fontFamily: '"Inter", "Roboto", "Segoe UI", sans-serif',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <h3 style={{ margin: '0 0 16px', fontWeight: 700, fontSize: 22 }}>Конструктор сценария</h3>

        <div style={{ background: 'white', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <input
            type="text"
            placeholder="Название сценария"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid #d9d9d9',
              marginBottom: 12, fontSize: 14,
            }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={addMenuNode} style={primaryBtn}>📋 Добавить меню</button>
            <button onClick={handleSave} style={saveBtn}>💾 Сохранить</button>
            <button onClick={onCancel} style={cancelBtn}>Отмена</button>
          </div>
        </div>

        <div style={{ height: 550, border: '1px solid #e0e0e0', borderRadius: 12, background: 'white', position: 'relative', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeDoubleClick={onNodeDoubleClick}
            onEdgeClick={onEdgeClick}
            nodeTypes={nodeTypes}
          >
            <Background color="#f0f0f0" gap={20} />
            <Controls />
          </ReactFlow>
          {selectedEdge && (
            <div style={{
              position: 'absolute', top: 12, right: 12, zIndex: 10,
              background: 'white', padding: '6px 12px', borderRadius: 6,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            }}>
              <button onClick={deleteSelectedEdge} style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4, color: '#cf1322', padding: '4px 10px', cursor: 'pointer' }}>
                🗑️ Удалить связь
              </button>
            </div>
          )}
        </div>

        {/* Модальное окно редактирования */}
        {editingNode && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
          }}>
            <div style={{
              background: 'white', padding: 24, borderRadius: 12, minWidth: 440, maxHeight: '85vh', overflow: 'auto',
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            }}>
              <h4 style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 18 }}>Редактировать меню</h4>

              <label style={labelStyle}>Текст меню</label>
              <textarea value={editLabel} onChange={(e) => setEditLabel(e.target.value)} style={{ ...inputStyle, minHeight: 70 }} />

              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>Формат текста</label>
                <select value={editFormat} onChange={(e) => setEditFormat(e.target.value)} style={inputStyle}>
                  <option value="">Обычный текст</option>
                  <option value="markdown">Markdown</option>
                  <option value="html">HTML</option>
                </select>
              </div>

              <div style={{ marginTop: 10 }}>
                <label style={labelStyle}>URL картинки</label>
                <input value={editImageUrl} onChange={(e) => setEditImageUrl(e.target.value)} style={inputStyle} placeholder="https://..." />
              </div>

              <div style={{ marginTop: 10 }}>
                {(!hasMainMenu || editIsMain) ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                    <input type="checkbox" checked={editIsMain} onChange={(e) => setEditIsMain(e.target.checked)} />
                    🏠 Это главное меню
                  </label>
                ) : (
                  <p style={{ fontSize: 12, color: '#888' }}>Главное меню уже задано в другом блоке.</p>
                )}
              </div>

              <h5 style={{ margin: '20px 0 12px', fontWeight: 600 }}>Кнопки</h5>
              {editOptions.map((opt, idx) => (
                <div key={idx} style={{ background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 10, border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <strong>Кнопка {idx + 1}</strong>
                    <div>
                      <button onClick={() => moveOption(idx, 'up')} disabled={idx === 0} style={smallBtn}>⬆️</button>
                      <button onClick={() => moveOption(idx, 'down')} disabled={idx === editOptions.length - 1} style={{ ...smallBtn, marginLeft: 4 }}>⬇️</button>
                    </div>
                  </div>
                  <input value={opt.text} onChange={(e) => updateOption(idx, 'text', e.target.value)} placeholder="Текст кнопки" style={{ ...inputStyle, marginBottom: 6 }} />
                  <select value={opt.type} onChange={(e) => updateOption(idx, 'type', e.target.value)} style={{ ...inputStyle, marginBottom: 6 }}>
                    <option value="callback">Callback (продолжить)</option>
                    <option value="link">Ссылка</option>
                  </select>
                  {opt.type === 'callback' ? (
                    <input value={opt.payload || ''} onChange={(e) => updateOption(idx, 'payload', e.target.value)} placeholder="Payload" style={inputStyle} />
                  ) : (
                    <input value={opt.url || ''} onChange={(e) => updateOption(idx, 'url', e.target.value)} placeholder="https://..." style={inputStyle} />
                  )}
                  <button onClick={() => removeOption(idx)} style={{ marginTop: 6, background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 4, color: '#cf1322', padding: '4px 10px', cursor: 'pointer' }}>🗑️ Удалить</button>
                </div>
              ))}
              <button onClick={addOption} style={{ ...secondaryBtn, marginTop: 4 }}>➕ Добавить кнопку</button>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button onClick={() => deleteNode(editingNode.id)} style={{ background: '#ff4d4f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>🗑️ Удалить блок</button>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveNodeChanges} style={primaryBtn}>Сохранить</button>
                  <button onClick={closeEditor} style={cancelBtn}>Отмена</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ScenarioBuilder: React.FC<Props> = (props) => (
  <ReactFlowProvider>
    <ScenarioBuilderInner {...props} />
  </ReactFlowProvider>
);

export default ScenarioBuilder;

/* ---------- Общие стили ---------- */
const primaryBtn: React.CSSProperties = {
  background: '#1677ff', color: 'white', border: 'none', padding: '8px 18px', borderRadius: 6,
  fontWeight: 600, cursor: 'pointer', fontSize: 13,
};
const saveBtn: React.CSSProperties = {
  background: '#52c41a', color: 'white', border: 'none', padding: '8px 18px', borderRadius: 6,
  fontWeight: 600, cursor: 'pointer', fontSize: 13,
};
const cancelBtn: React.CSSProperties = {
  background: '#f5f5f5', border: '1px solid #d9d9d9', padding: '8px 18px', borderRadius: 6,
  cursor: 'pointer', fontWeight: 500, fontSize: 13,
};
const secondaryBtn: React.CSSProperties = {
  background: '#f5f5f5', border: '1px solid #d9d9d9', padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
};
const smallBtn: React.CSSProperties = {
  background: 'white', border: '1px solid #d9d9d9', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 12,
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, color: '#555', marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 14, boxSizing: 'border-box',
};